import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/16/solid";
import { ComponentEnum, LessonI } from "../../../types";
import Button from "../../../componentLibrary/Button";
import Modal from "../../../componentLibrary/Modal";
import Progress from "../../../components/Progress";
import { renderPageComponents } from "../utils/renderComponent";
import { parsePreviousAnswers } from "../utils/parsePreviousAnswers";
import { PriorityListRef } from "./contentComponents/PriorityListComponent";
import { ForEachComponentRef } from "./contentComponents/ForEachComponent";

export default function PreviewLessonModal({
  lesson,
  isModalOpen,
  setIsModalOpen,
  currentPageId,
}: {
  lesson: LessonI;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentPageId: string;
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [lessonResponse, setLessonResponse] = useState<Record<string, any>>({});
  const [previewPage, setPreviewPage] = useState(0);
  const lastChangedVariable = useRef<string | null>(null);

  // Storing PriorityList component refs to call function present inside the component
  const priorityListRefs = useRef<Record<string, PriorityListRef | null>>({});

  // Create refs for components
  const forEachRefs = useRef<Record<string, ForEachComponentRef | null>>({});

  const synchForEach = (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answers: Record<string, any>,
  ) => {
    Object.entries(forEachRefs.current).forEach(([, ref]) => {
      if (ref && typeof ref.syncVariableChange === "function") {
        ref.syncVariableChange(variable, answers);
      }
    });
  };

  const synchPriorityList = (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answers: Record<string, any>,
  ) => {
    Object.entries(priorityListRefs.current).forEach(([, ref]) => {
      if (ref && typeof ref.syncVariableChange === "function") {
        ref.syncVariableChange(variable, answers);
      }
    });
  };

  useEffect(() => {
    if (lastChangedVariable.current) {
      synchForEach(lastChangedVariable.current, lessonResponse);
      synchPriorityList(lastChangedVariable.current, lessonResponse);
      lastChangedVariable.current = null;
    }
  }, [lessonResponse]);

  const handleContentChange = (
    variable: string,
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    displayAs?: string,
  ) => {
    lastChangedVariable.current = variable;
    setLessonResponse((prev) => {
      const newState = { ...prev };

      if (value === undefined) {
        delete newState[variable];
      } else {
        newState[variable] = {
          label: label,
          value: value,
          displayAs,
        };
      }
      return newState;
    });
  };

  const handlePageChange = (direction: "next" | "back") => {
    const nextPageIndex =
      direction === "next" ? previewPage + 1 : previewPage - 1;
    if (nextPageIndex < 0) {
      setIsModalOpen(false);
      return;
    }
    setPreviewPage(nextPageIndex);
  };

  useEffect(() => {
    if (currentPageId) {
      const idx = lesson.pages.findIndex((page) => page._id === currentPageId);
      setPreviewPage(idx === -1 ? 0 : idx);
    }
  }, [currentPageId]);

  return (
    <Modal
      title="Preview Lesson"
      size="lg"
      height="90vh"
      isOpen={isModalOpen}
      onCloseModal={() => {
        setIsModalOpen(false);
        setPreviewPage(0);
      }}
    >
      <div className="pt-2 pb-4 px-4 bg-white w-full rounded-lg shadow-md border border-[#E0E0E0]">
        {previewPage >= lesson.pages.length ? (
          <>
            <div>
              <Button
                variant="alternative"
                size="sm"
                onClick={() => setPreviewPage(previewPage - 1)}
              >
                <ArrowLeftIcon color="black" className="h-5 w-5" />
              </Button>
              <p className="font-medium my-2">Congratulations!</p>
              <h2 className="my-2">
                {`You've completed the ${lesson.title} lesson.`}
              </h2>
              {lessonResponse && Object.keys(lessonResponse).length > 0 && (
                <div className="space-y-4">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {Object.values(lessonResponse).map((response: any, i) => (
                    <div key={i}>
                      <p className="font-semibold">
                        {parsePreviousAnswers(response.label, lessonResponse)}
                      </p>
                      {response.displayAs ? (
                        <p>{response.displayAs}</p>
                      ) : (
                        <>
                          {Array.isArray(response.value) ? (
                            <ul className="list-disc pl-5">
                              {response.value.map((item, index) => (
                                <li key={index}>
                                  {parsePreviousAnswers(item, lessonResponse)}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p
                              style={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {parsePreviousAnswers(
                                response.value,
                                lessonResponse,
                              )}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button>Finish</Button>
          </>
        ) : (
          <>
            <div>
              <div>
                {previewPage !== 0 && (
                  <Button
                    variant="alternative"
                    size="sm"
                    onClick={() => setPreviewPage(previewPage - 1)}
                  >
                    <ArrowLeftIcon color="black" className="h-5 w-5" />
                  </Button>
                )}
              </div>
              <p className="font-medium my-2 capitalize">{lesson.title}</p>
              {lesson.pages.length > 0 && (
                <h2 className="mb-2 capitalize">
                  {lesson.pages[previewPage].title}
                </h2>
              )}
              <Progress
                currentStep={previewPage}
                steps={lesson.pages.map((page) => page.title)}
                onClick={setPreviewPage}
              />
              <div className="mt-4 mb-4 space-y-4">
                {renderPageComponents(
                  lesson.pages[previewPage].components,
                  previewPage,
                  lessonResponse,
                  lessonResponse,
                  handleContentChange,
                  {
                    [ComponentEnum.LESSON_LINK]: {
                      disabled: true,
                    },
                    [ComponentEnum.FOR_EACH]: {
                      onRefAssign: (
                        componentId: string,
                        ref: ForEachComponentRef | null,
                      ) => {
                        forEachRefs.current[componentId] = ref;
                      },
                    },
                    [ComponentEnum.PRIORITY_LIST]: {
                      onRefAssign: (
                        componentId: string,
                        ref: PriorityListRef | null,
                      ) => {
                        priorityListRefs.current[componentId] = ref;
                      },
                    },
                  },
                  "textarea",
                )}
              </div>
            </div>
            <div className="flex space-x-4 w-full px-4">
              <Button
                className="w-1/2"
                variant="secondary"
                onClick={() => handlePageChange("back")}
              >
                Back
              </Button>
              <Button
                className="flex-1 w-1/2"
                onClick={() => handlePageChange("next")}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
